// Adds createWritable to FileSystemFileHandle in Safari
import 'native-file-system-adapter'

export function isFileHandle(handle: FileSystemHandle): handle is FileSystemFileHandle {
  return handle.kind === 'file'
}

export function isDirectoryHandle(handle: FileSystemHandle): handle is FileSystemDirectoryHandle {
  return handle.kind === 'directory'
}

export async function visitHandles(
  root: FileSystemHandle,
  visitor: (path: string, handle: FileSystemHandle) => Promise<boolean | undefined>
): Promise<boolean | undefined> {
  let stop = await visitor(root.name, root)
  if (stop) {
    return true
  }
  if (isDirectoryHandle(root)) {
    for await (const child of root.values()) {
      stop = await visitHandles(child, async (path, handle) => {
        return await visitor(`${root.name}/${path}`, handle)
      })
      if (stop) {
        return true
      }
    }
  }
}

export async function verifyPermission(handle: FileSystemHandle, mode: FileSystemPermissionMode) {
  // queryPermission and requestPermission not available in Firefox
  // https://developer.mozilla.org/en-US/docs/Web/API/FileSystemHandle/queryPermission
  if (!('queryPermission' in handle) || !('requestPermission' in handle)) {
    return true // assume permission granted in case of OPFS, or rely on popup to appear
  }

  // Check if we already have permission, if so, return true.
  if ((await handle.queryPermission({ mode })) === 'granted') {
    return true
  }
  // Request permission to the file, if the user grants permission, return true.
  if ((await handle.requestPermission({ mode })) === 'granted') {
    return true
  }
  // The user did not grant permission, return false.
  return false
}

export async function createFile(
  parentDirectory: FileSystemDirectoryHandle,
  name: string,
  data?: FileSystemWriteChunkType
) {
  try {
    const existingFile = await parentDirectory.getFileHandle(name, {
      create: false
    })
    throw new Error(`Cannot create file: ${existingFile.name} already exists`)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'NotFoundError') {
      const createdFile = await parentDirectory.getFileHandle(name, {
        create: true
      })

      if (data) {
        const writable = await createdFile.createWritable()
        await writable.truncate(0)
        await writable.write(data)
        await writable.close()
      }

      return createdFile
    } else {
      throw error
    }
  }
}

export async function createDirectory(parentDirectory: FileSystemDirectoryHandle, name: string) {
  try {
    const existingDirectory = await parentDirectory.getDirectoryHandle(name, {
      create: false
    })
    throw new Error(`Cannot create directory: ${existingDirectory.name} already exists`)
  } catch (error) {
    if (error instanceof DOMException && error.name === 'NotFoundError') {
      return await parentDirectory.getDirectoryHandle(name, {
        create: true
      })
    } else {
      throw error
    }
  }
}

export async function copyFile(
  fileHandle: FileSystemFileHandle,
  newParentDirectory: FileSystemDirectoryHandle,
  newName: string
) {
  const file = await fileHandle.getFile()
  const text = await file.text()
  return await createFile(newParentDirectory, newName, text)
}

export async function copyDirectory(
  directory: FileSystemDirectoryHandle,
  newParentDirectory: FileSystemDirectoryHandle,
  newName: string
) {
  if (await directory.isSameEntry(await navigator.storage.getDirectory())) {
    throw new Error('Cannot copy file system root')
  }

  const copiedDirectory = await createDirectory(newParentDirectory, newName)

  for await (const [childName, child] of directory.entries()) {
    if (child.kind === 'file') {
      await copyFile(child, copiedDirectory, childName)
    } else {
      await copyDirectory(child, copiedDirectory, childName)
    }
  }

  return copiedDirectory
}

export async function renameFile(
  fileHandle: FileSystemFileHandle,
  parentDirectory: FileSystemDirectoryHandle,
  newName: string
) {
  if (fileHandle.name === newName) {
    return
  }

  if ('move' in fileHandle && typeof fileHandle.move === 'function') {
    try {
      await fileHandle.move(newName)
    } catch (error) {
      console.debug(`rename file failed with error (${error}), falling back to copy+remove`)
      await copyFile(fileHandle, parentDirectory, newName)
      await removeEntry(fileHandle, parentDirectory)
    }
  } else {
    await copyFile(fileHandle, parentDirectory, newName)
    await removeEntry(fileHandle, parentDirectory)
  }
}

export async function renameDirectory(
  directory: FileSystemDirectoryHandle,
  parentDirectory: FileSystemDirectoryHandle,
  newName: string
) {
  if (directory.name === newName) {
    return
  }

  if ('move' in directory && typeof directory.move === 'function') {
    await directory.move(newName)
  } else {
    await copyDirectory(directory, parentDirectory, newName)
    await removeEntry(directory, parentDirectory)
  }
}

export async function moveFile(
  fileHandle: FileSystemFileHandle,
  oldParentDirectory: FileSystemDirectoryHandle,
  newParentDirectory: FileSystemDirectoryHandle
) {
  if ('move' in fileHandle && typeof fileHandle.move === 'function') {
    try {
      await fileHandle.move(newParentDirectory)
    } catch (error) {
      console.debug(`move file failed with error (${error}), falling back to copy+remove`)
      await copyFile(fileHandle, newParentDirectory, fileHandle.name)
      await removeEntry(fileHandle, oldParentDirectory)
    }
  } else {
    await copyFile(fileHandle, newParentDirectory, fileHandle.name)
    await removeEntry(fileHandle, oldParentDirectory)
  }
}

export async function moveDirectory(
  directory: FileSystemDirectoryHandle,
  oldParentDirectory: FileSystemDirectoryHandle,
  newParentDirectory: FileSystemDirectoryHandle
) {
  if ('move' in directory && typeof directory.move === 'function') {
    await directory.move(newParentDirectory)
  } else {
    await copyDirectory(directory, newParentDirectory, directory.name)
    await removeEntry(directory, oldParentDirectory, { recursive: true })
  }
}

export async function removeEntry(
  handle: FileSystemHandle,
  parentDirectory: FileSystemDirectoryHandle,
  options?: { recursive?: boolean }
) {
  await parentDirectory.removeEntry(handle.name, options)
}
